import type { ReactNode } from "react";
import { Link } from "react-router";
import { AttendeeSyncIcon } from "@/components/brand/attendee-sync-icon";
import { BrandMark } from "@/components/brand-mark";
import { APP_VERSION } from "@/lib/app-version";

type AuthShellProps = {
  title?: string;
  description?: string;
  children: ReactNode;
  /** Content rendered below the form panel (e.g. alternate sign-in). */
  belowPanel?: ReactNode;
  footer?: ReactNode;
};

/** Full-bleed split shell: emblem left, wordmark + form rail right. */
export function AuthShell({
  title,
  description,
  children,
  belowPanel,
  footer,
}: AuthShellProps) {
  const hasIntro = Boolean(title || description);
  const legalFooter = (
    <p>
      <a href="/legal/privacy.html" target="_blank" rel="noreferrer">
        Privacy Policy
      </a>
      <span aria-hidden="true"> · </span>
      <a href="/legal/terms.html" target="_blank" rel="noreferrer">
        Terms of Service
      </a>
    </p>
  );

  return (
    <div className="rs-auth">
      <aside className="rs-auth-brand-col" aria-hidden>
        <AttendeeSyncIcon decorative className="rs-auth-emblem" />
      </aside>

      <div className="rs-auth-form-col">
        <div className="rs-auth-form-inner">
          <header className="rs-auth-hero dark">
            <Link
              to="/"
              className="rs-auth-wordmark-link text-inherit no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rs-info"
              aria-label="AttendeeSync Dashboard"
            >
              <BrandMark
                size="lg"
                showIcon={false}
                className="rs-auth-wordmark"
              />
            </Link>
            {hasIntro ? (
              <>
                {title ? <h1 className="rs-auth-title">{title}</h1> : null}
                {description ? (
                  <p className="rs-auth-lede">{description}</p>
                ) : null}
              </>
            ) : null}
          </header>

          <div className="rs-auth-panel">{children}</div>

          {belowPanel ? (
            <div className="rs-auth-below">{belowPanel}</div>
          ) : null}

          <div className="rs-auth-footer">{footer ?? legalFooter}</div>
        </div>
      </div>

      <p className="rs-auth-version" aria-label={`Version ${APP_VERSION}`}>
        v{APP_VERSION}
      </p>
    </div>
  );
}
